/**
 * Copy + pure helpers for duplicate clients (BE docs/client-duplicates.md §E,
 * §F). Refusals arrive as a `code`; the app never shows the server's
 * responseMessage. No copy here mentions support.
 */

// §F: one calm line per refusal code. Unknown codes fall back to `invalid`.
export const REFUSAL_COPY = {
    both_have_login: {
        title: 'These are two separate accounts',
        body: 'Each logs in to Beu, so they stay as two clients. Their bookings aren\'t affected.',
    },
    staff_account: {
        title: 'This one can\'t be merged',
        body: 'This person is on a team on Beu, so their record can\'t be merged into a client.',
    },
    separate_records: {
        title: 'These records stay separate',
        body: 'These two records need to stay separate. Their bookings aren\'t affected.',
    },
    co_held_private: {
        title: 'This one can\'t be merged',
        body: 'This client is private to another stylist, so they can\'t be merged.',
    },
    private_client: {
        title: 'This one can\'t be merged',
        body: 'This client is private to another stylist, so they can\'t be merged.',
    },
    // App-only: a self-employed stylist picked a client outside their own book.
    own_book_only: {
        title: 'Pick two of your own clients',
        body: 'You can merge clients in your own book. The salon owner, or anyone with All clients, merges the salon\'s shared clients.',
    },
    busy: {
        title: 'Someone\'s merging these right now',
        body: 'Someone\'s merging these clients right now. Try again in a minute.',
    },
    no_permission: {
        title: 'Your role can\'t merge clients',
        body: 'Your role can\'t merge clients. The salon owner can change this in Team › Permissions.',
    },
    paused: {
        title: 'Merging is paused',
        body: 'Merging clients is paused for a few days while we improve it. Nothing has changed on your clients.',
    },
    undo_expired: {
        title: 'This merge can\'t be undone now',
        body: 'Merges can be undone for 72 hours.',
    },
    undo_not_exact: {
        title: 'This merge can\'t be undone',
        body: 'Something has changed on this client since the merge (for example a reward was used), so this merge can\'t be undone.',
    },
    undo_by_beu: {
        title: 'Beu made this merge',
        body: 'Beu made this merge for you, so we undo it too. Message us if it needs undoing.',
    },
    rate_limited: {
        title: 'Give it a moment',
        body: 'That was a lot of merges at once. Try again in a minute.',
    },
    already_merged: {
        title: 'These records have changed',
        body: 'One of these records has changed since you opened it. Close this to see the latest.',
    },
    invalid: {
        title: 'Something went wrong',
        body: 'Something went wrong merging these clients. Nothing was changed.',
    },
};

// The list just refreshes: these mean "state moved on", not an error.
export const SILENT_CODES = ['already_merged', 'superseded', 'not_found'];

export function refusalCopy(code) {
    return REFUSAL_COPY[code] || REFUSAL_COPY.invalid;
}

/** Short reason used inside the partial-failure line of the result toast. */
export function shortReason(code) {
    if (SILENT_CODES.includes(code)) return 'it had already changed';
    const c = refusalCopy(code);
    return c.title.charAt(0).toLowerCase() + c.title.slice(1);
}

export const SIGNAL_LABEL = {
    email: 'Same email',
    phone: 'Same phone',
    email_phone: 'Same email and phone',
};

export const PICK_FIELDS = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phoneNumber', label: 'Phone' },
    { key: 'dob', label: 'Date of birth' },
];

/**
 * Default picks (§E): the kept record's value unless blank, then the first
 * other record that has one. Returns { field: userId }.
 */
export function defaultPicks(group, keepUserId) {
    const keep = group.members.find((m) => m.userId === keepUserId) || group.members[0];
    const others = group.members.filter((m) => m !== keep);
    const picks = {};
    PICK_FIELDS.forEach(({ key }) => {
        if (keep && keep[key]) { picks[key] = keep.userId; return; }
        const o = others.find((m) => m[key]);
        picks[key] = o ? o.userId : (keep && keep.userId);
    });
    return picks;
}

/** Distinct non-blank values per field across the group (for "Check details"). */
export function fieldOptions(group, key) {
    const seen = new Map();
    group.members.forEach((m) => {
        const v = (m[key] || '').trim();
        if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), { value: v, userId: m.userId });
    });
    return Array.from(seen.values());
}

/** Fields whose values differ across the group: flagged on the card. */
export function differingFields(group) {
    return PICK_FIELDS.filter(({ key }) => fieldOptions(group, key).length > 1).map((f) => f.key);
}

/** The login email is the client's own: never pickable by a salon. */
export function emailLocked(group, keepUserId) {
    const keep = group.members.find((m) => m.userId === keepUserId);
    return !!(keep && keep.hasLogin && keep.email);
}

/** Picks as the wire wants them: values, never other people's ids. */
export function picksToValues(group, picks, keepUserId) {
    const out = {};
    PICK_FIELDS.forEach(({ key }) => {
        if (key === 'email' && emailLocked(group, keepUserId)) return;
        const src = group.members.find((m) => m.userId === picks[key]);
        if (src && src[key]) out[key] = src[key];
    });
    return out;
}

export function plural(n, one, many) {
    return `${n} ${n === 1 ? one : (many || one + 's')}`;
}

/** "5 merged." / "5 merged. 1 couldn't be merged: …" */
export function resultLine(results) {
    const ok = results.filter((r) => r.ok);
    const bad = results.filter((r) => !r.ok && !SILENT_CODES.includes(r.code));
    const head = ok.length === 1 ? '1 duplicate merged' : `${ok.length} duplicates merged`;
    if (!bad.length) return head;
    const reasons = Array.from(new Set(bad.map((r) => shortReason(r.code))));
    return `${ok.length} merged. ${bad.length} couldn't be merged: ${reasons.join('; ')}.`;
}

/** Number of client records that disappear into survivors. */
export function clientsMerged(groups) {
    return groups.reduce((n, g) => n + Math.max(0, g.members.length - 1), 0);
}

/** Records across the chosen groups: a group of 3 is "3 records", one client. */
export function recordsIn(groups) {
    return groups.reduce((n, g) => n + g.members.length, 0);
}

/** "twice" / "3 times": how many records one client has. */
export function timesOnHere(n) {
    return n === 2 ? 'twice' : `${n} times`;
}

export function formatUndoUntil(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    const time = d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' }).replace(' ', '').toLowerCase();
    return `${day}, ${time}`;
}
